import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useSessionStore } from 'store/session';
import { useUserStore } from 'store/user';

import type { ResourceError, ResourceName, ResourcePayload } from './resources';
import type { Params as ApiFetchParams } from './useApiFetch';
import useApiFetch, { useDasApiFetch } from './useApiFetch';

export interface Params<R extends ResourceName, E = unknown>
  extends ApiFetchParams<R> {
  queryOptions?: Omit<
  UseQueryOptions<ResourcePayload<R>, ResourceError<E>, ResourcePayload<R>>,
  'queryKey' | 'queryFn'
  >;
}

export function getResourceKey<R extends ResourceName>(
  resource: R,
  { pathParams, queryParams }: Params<R> = {},
) {
  if (pathParams || queryParams) {
    return [ resource, { ...pathParams, ...queryParams } ];
  }

  return [ resource ];
}

export function useDasApiQuery<R extends ResourceName, E = unknown>(
  resource: R,
  { queryOptions, pathParams, queryParams, fetchParams }: Params<R, E> = {},
) {
  const apiFetch = useDasApiFetch();
  const { session } = useSessionStore();
  const { userRole } = useUserStore();
  let enabled = true;
  if (queryOptions?.enabled !== undefined) {
    enabled = queryOptions?.enabled;
  }

  return useQuery<ResourcePayload<R>, ResourceError<E>, ResourcePayload<R>>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getResourceKey(resource, { pathParams, queryParams }),
    queryFn: async() => {
      // all errors and error typing is handled by react-query
      // so error response will never go to the data
      // that's why we are safe here to do type conversion "as Promise<ResourcePayload<R>>"
      return apiFetch(resource, {
        pathParams,
        queryParams,
        fetchParams,
        session,
      }) as Promise<ResourcePayload<R>>;
    },
    ...queryOptions,
    enabled: Boolean(enabled && Boolean(session) && userRole === 'USER'),
  });
}

export default function useApiQuery<R extends ResourceName, E = unknown>(
  resource: R,
  { queryOptions, pathParams, queryParams, fetchParams }: Params<R, E> = {},
) {
  const apiFetch = useApiFetch();
  const { session } = useSessionStore();
  const { userRole } = useUserStore();
  let enabled = true;
  if (queryOptions?.enabled !== undefined) {
    enabled = queryOptions?.enabled;
  }
  return useQuery<ResourcePayload<R>, ResourceError<E>, ResourcePayload<R>>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getResourceKey(resource, { pathParams, queryParams }),
    queryFn: async() => {
      // all errors and error typing is handled by react-query
      // so error response will never go to the data
      // that's why we are safe here to do type conversion "as Promise<ResourcePayload<R>>"
      return apiFetch(resource, {
        pathParams,
        queryParams,
        fetchParams,
        session,
      }) as Promise<ResourcePayload<R>>;
    },
    ...queryOptions,
    enabled: Boolean(
      enabled &&
        Boolean(session) &&
        (userRole === 'ADMIN' ||
          resource === 'contract_verification_config' ||
          resource === 'contract'),
    ),
  });
}
